import { forwardRef, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "@gatsbyjs/reach-router";
import { navigate } from "gatsby";

import { withALErrorBoundary } from "../helpers/ErrorBoundary/ALErrorBoundary";
import { CartContext } from "../context/CartProvider";
import { LuxeContext } from "../context/LuxeProvider";
import { useAuth } from "../context/AuthProvider";
import { gaCustomEvent } from "@helpers/browser";
import { setGorgiasChatVisibility } from "@helpers/gorgias";
import { cn } from "@helpers/general";
import useSticky from "../hooks/useSticky";
import useResponsiveWithHydrationFix from "@hooks/useResponsiveWithHydrationFix";
import { useCurrentCountryCode } from "../hooks/usePrices";
import useDeviceDetect from "@hooks/useDeviceDetect";
import useScrollDirection from "@hooks/useScrollDirection";

import { Link } from "./al_components/ALLink";
import { ALLogo } from "./ALComponents";
import DesktopMenu from "./header/DesktopMenu";
import MobileMenu from "./header/MobileMenu";
import SearchPopup from "./header/SearchPopup";
import GiftWithPurchaseBlock from "./GiftWithPurchaseBlock";

import { getShopifyStoreByCountry } from "../../locale-shopifies";

import IconAccount from "@svgs/account2025.svg";
import IconCart from "@svgs/cart2025.svg";
import IconSearch from "@svgs/search2025.svg";
import IconFaq from "@svgs/faq2025.svg";
import IconMenu from "@svgs/menu2025.svg";

import "./header/styles.scss";

/**
 * We use useEffect to get the country code cookie to prevent a strange prod-only error
 * on the FAQ link where the href remains the value at the build time.
 *
 * @returns
 */
function useCurrentShopifyStore() {
  const isoCountryCode = useCurrentCountryCode();
  return getShopifyStoreByCountry(isoCountryCode);
}

const Header = forwardRef(
  ({ searchPopupVisible, setSearchPopupVisible, displayNavigation, setDisplayNavigation }, ref) => {
    const { itemsInCart, toggleCart, isSideCartOpen } = useContext(CartContext);
    const { knowledgeBase } = useCurrentShopifyStore();
    const { accountDetails } = useAuth();
    const { luxe } = useContext(LuxeContext);
    const { subscriptionCredit, isLuxeActive } = luxe;
    const offsetHeader = useSticky(true);
    const location = useLocation();
    const { isMobile } = useDeviceDetect();
    const isLpPdpPage = location.pathname.includes("/lp/");
    const isProductPage = location.pathname.includes("/products/");
    const scrollDirection = useScrollDirection(isLpPdpPage || isProductPage);

    const closeSearchBar = useCallback(() => setSearchPopupVisible(false), [setSearchPopupVisible]);
    const onMenuClick = useCallback((label) => gaCustomEvent("Menu Link", "Click", label), []);
    const handleOnClickFAQ = useCallback(() => onMenuClick("FAQ"), [onMenuClick]);

    // Close the search popup on route change
    useEffect(() => {
      closeSearchBar();
    }, [location]);

    const mobileMenuOpenRef = useRef(null);
    const mobileMenuCloseRef = useRef(null);
    const [mobileMenuIsOpen, setMobileMenuIsOpen] = useState(false);

    const showGWPBlock =
      !isMobile &&
      !searchPopupVisible &&
      !location.pathname.includes("/byob-") &&
      (location.pathname.includes("/products/") ||
        location.pathname.includes("/collections/") ||
        location.pathname.includes("/p/"));

    const handleToggleMenu = useCallback(
      () => setMobileMenuIsOpen(!mobileMenuIsOpen),
      [mobileMenuIsOpen]
    );

    const goToAccount = () => {
      navigate(isLuxeActive ? "/ana-luisa-luxe/" : "/account/");
    };

    useEffect(() => {
      if (
        mobileMenuIsOpen &&
        mobileMenuIsOpen.current &&
        mobileMenuCloseRef &&
        mobileMenuCloseRef.current
      ) {
        if (mobileMenuIsOpen && mobileMenuIsOpen.current) {
          mobileMenuCloseRef.current.focus();
        } else {
          mobileMenuOpenRef.current.focus();
        }
      }
      setGorgiasChatVisibility(!mobileMenuIsOpen);
    }, [mobileMenuIsOpen]);

    useEffect(() => {
      if (isMobile && (isLpPdpPage || isProductPage)) {
        if (scrollDirection === "down" && displayNavigation) {
          setDisplayNavigation(false);
        } else if (scrollDirection === "up" && !displayNavigation) {
          setDisplayNavigation(true);
        }
      }
    }, [
      isLpPdpPage,
      isProductPage,
      scrollDirection,
      displayNavigation,
      setDisplayNavigation,
      isMobile,
    ]);

    const onSearchClick = () => {
      onMenuClick("SEARCH");
      setSearchPopupVisible((v) => !v);
    };

    const headerContent = useResponsiveWithHydrationFix(
      <div className="flex w-full justify-between desktop:hidden">
        <div className="flex items-center gap-[15px]">
          <button
            onClick={handleToggleMenu}
            ref={mobileMenuOpenRef}
            aria-labelledby="menu-button-label"
            type="button"
          >
            <span id="menu-button-label" hidden>
              Menu
            </span>
            <IconMenu width="16" stroke="#000000" />
          </button>

          <MobileMenu
            toggleSlideout={handleToggleMenu}
            isOpen={mobileMenuIsOpen}
            ref={mobileMenuCloseRef}
            aria-expanded={mobileMenuIsOpen}
          />
          <Link aria-label="ana luisa home" to="/">
            <ALLogo className="h-[18px]" />
          </Link>
        </div>

        <div className="flex items-center gap-[20px]">
          <button
            onClick={() => setSearchPopupVisible((v) => !v)}
            aria-labelledby="search-button-label"
            type="button"
          >
            <IconSearch width="16" height="16" stroke="#000000" fill="none" />
          </button>
          {subscriptionCredit > 0 && accountDetails ? (
            <button
              className="flex items-center gap-[7px]"
              type="button"
              onClick={goToAccount}
              aria-label="Go to account"
            >
              <IconAccount height="16px" width="16px" stroke="#000000" />
              <div className="typo-micro font-bold underline border border-black rounded-[4px] px-[7px] py-[3px]">
                ${Math.floor(subscriptionCredit)}&nbsp;credits
              </div>
            </button>
          ) : (
            <Link to="/account" aria-label="account">
              <IconAccount width="16px" height="16px" stroke="#000000" />
            </Link>
          )}
          <button
            className="relative"
            onClick={toggleCart}
            aria-label="cart"
            tabIndex={0}
            type="button"
          >
            <IconCart stroke="#000000" />
            {itemsInCart > 0 && (
              <span className="absolute text-[7px] font-bold top-[7px] left-[7px]">
                {itemsInCart}
              </span>
            )}
          </button>
        </div>
      </div>,
      <>
        <Link
          id="header-logo"
          className="flex w-full mobile:hidden pt-[25px]"
          aria-label="ana luisa home"
          to="/"
        >
          <ALLogo className="h-[37px]" />
        </Link>

        <div className="w-full flex justify-between pt-[25px] pb-[20px] mobile:hidden">
          <DesktopMenu onShowMenu={closeSearchBar} />
          <div className="flex items-center gap-[30px]">
            <button
              type="button"
              className={`flex border-b border-b-black min-w-[180px] gap-[10px] py-[5px] ${
                !searchPopupVisible ? "z-[2]" : ""
              }`}
              onClick={onSearchClick}
            >
              <IconSearch width="17" height="17" fill="none" stroke="#000000" />
              <span className="typo-micro font-bold">Search</span>
            </button>
            <button
              type="button"
              className="typo-micro font-bold"
              onClick={() => navigate("/pages/retail/")}
            >
              Stores
            </button>
            <div className="pt-[2px]" aria-label="help">
              <a href={knowledgeBase} target="_blank" onClick={handleOnClickFAQ} rel="noreferrer">
                <IconFaq width="22px" height="22px" fill="none" />
              </a>
            </div>

            <button
              className="header_account py-[5px] relative group"
              aria-label="account"
              role="menu"
              tabIndex={0}
              type="button"
              onClick={goToAccount}
            >
              <IconAccount width="20px" height="20px" stroke="#000000" />
            </button>
            {subscriptionCredit > 0 && accountDetails && (
              <button
                className="ml-[-20px] typo-micro font-bold underline border border-black rounded-[4px] px-[7px] py-[3px]"
                onClick={goToAccount}
                type="button"
              >
                ${Math.floor(subscriptionCredit)}&nbsp;credits
              </button>
            )}

            <button
              className="relative"
              onClick={toggleCart}
              aria-label="cart"
              tabIndex={0}
              type="button"
            >
              <IconCart width="21px" height="21px" stroke="#000000" />
              {itemsInCart > 0 && (
                <span className="absolute text-[9px] top-[7px] left-[8px] font-bold">
                  {itemsInCart}
                </span>
              )}
            </button>
          </div>
        </div>
      </>
    );

    return (
      <header
        ref={ref}
        id="header"
        className={cn(
          "sticky bg-white border-b border-dark-grey transition-all ease-out duration-300",
          displayNavigation ? "" : "-translate-y-full"
        )}
        style={{
          ...offsetHeader,
          zIndex: searchPopupVisible || isSideCartOpen || mobileMenuIsOpen ? 999 : 98, // ensure cart and search gets above the AB
        }}
      >
        <nav
          className="header px-[1.74vw] mobile:px-[15px] mobile:py-[18px]"
          role="navigation"
          aria-label="main navigation"
        >
          {headerContent}
        </nav>

        {searchPopupVisible ? <SearchPopup closeSearch={closeSearchBar} /> : null}

        {showGWPBlock && <GiftWithPurchaseBlock />}
      </header>
    );
  }
);

export default withALErrorBoundary({
  name: "Header",
  priority: "P2",
})(Header);
